// Client Authentication Service
class ClientAuthService {
  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'https://api.kgcinnovations.com/api/v2';
    this.tokenKey = 'client_auth_token';
    this.clientKey = 'client_auth_user';
  }

  // Login
  async login(email, password) {
    try {
      const response = await fetch(`${this.baseURL}/auth/sign-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      // console.log('Login response data:', JSON.stringify(data));
      // alert("hello")

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if(data.data.user.role === 'admin') {
        throw new Error('Unauthorized: Client access required');
      }

      console.log('Login successful, storing token and client data');

      // Store token and client data
      this.setToken(data.data.token);
      this.setClient(data.data.client);

      return data.data;
    } catch (error) {
      console.error('Client login error:', error);
      throw error;
    }
  }

  // Logout
  async logout() {
    try {
      // Call logout endpoint if we have a token
      const token = this.getToken();
      if (token) {
        await fetch(`${this.baseURL}/auth/sign-out`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Client logout error:', error);
      // Continue with local logout even if server call fails
    } finally {
      // Always clear local storage
      this.clearAuth();
    }
  }

  // Check if client is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const client = this.getClient();
    return !!(token && client);
  }

  // Get current client
  getCurrentClient() {
    return this.getClient();
  }

  // Get auth token for API requests
  getAuthToken() {
    return this.getToken();
  }

  // Set authentication token
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  // Get authentication token
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Set client data
  setClient(client) {
    localStorage.setItem(this.clientKey, JSON.stringify(client));
  }

  // Get client data
  getClient() {
    const clientData = localStorage.getItem(this.clientKey);
    return clientData ? JSON.parse(clientData) : null;
  }

  // Clear all auth data
  clearAuth() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.clientKey);
  }

  // Get auth headers for API requests
  getAuthHeaders() {
    const token = this.getToken();
    if (token) {
      // bearer token
      return { 'Authorization': `Bearer ${token}` };
      // return { 'X-Client-Token': token };
    }
    return {};
  }

  // Verify token with server
  async verifyToken() {
    try {
      const token = this.getToken();
      if (!token) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        this.clearAuth();
        return false;
      }

      const data = await response.json();
      // Update client data if server returned updated info
      if (data.data) {
        this.setClient(data.data.client);
      }

      return true;
    } catch (error) {
      console.error('Client token verification error:', error);
      this.clearAuth();
      return false;
    }
  }

  // Client Data API calls
  async getInvoices() {
    return this.clientRequest('/invoices');
  }

  async getQuotes() {
    return this.clientRequest('/quotes');
  }

  async getDocuments() {
    return this.clientRequest('/client-documents/me');
  }

  async getProfile() {
    return this.clientRequest('/clients/me');
  }

  async updateProfile(profileData) {
    return this.clientRequest('/clients/me', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async getEmailRecipients() {
    return this.clientRequest('/client-email-recipients/me');
  }

  async addEmailRecipient(data) {
    return this.clientRequest('/client-email-recipients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmailRecipient(recipientId, data) {
    return this.clientRequest(`/client-email-recipients/${recipientId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailRecipient(recipientId) {
    return this.clientRequest(`/client-email-recipients/${recipientId}`, {
      method: 'DELETE',
    });
  }

  async getInvoice(invoiceId) {
    return this.clientRequest(`/invoices/${invoiceId}`);
  }

  async getQuote(quoteId) {
    return this.clientRequest(`/quotes/${quoteId}`);
  }

  async approveQuote(quoteId) {
    return this.clientRequest(`/quotes/approve/${quoteId}`, {
      method: 'POST',
    });
  }

  async rejectQuote(quoteId) {
    return this.clientRequest(`/quotes/reject/${quoteId}`, {
      method: 'POST',
    });
  }

  async downloadInvoice(invoiceId) {
    const token = this.getToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Use the client PDF endpoint with authentication
    const url = `${this.baseURL}/pdfs?type=invoice&id=${invoiceId}`;
    const authHeaders = this.getAuthHeaders();
    
    try {
      const response = await fetch(url, {
        headers: authHeaders
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `INVOICE-${invoiceId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Invoice download failed:', error);
      throw error;
    }
  }

  async downloadQuote(quoteId) {
    const token = this.getToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Use the client PDF endpoint with authentication
    const url = `${this.baseURL}/pdfs?type=quote&id=${quoteId}`;
    const authHeaders = this.getAuthHeaders();
    
    try {
      const response = await fetch(url, {
        headers: authHeaders
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `QUOTE-${quoteId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Quote download failed:', error);
      throw error;
    }
  }

  async downloadDocument(documentId) {
    const authHeaders = this.getAuthHeaders();

    try {
      const response = await fetch(`${this.baseURL}/client-documents/download/${documentId}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      const link = document.createElement('a');
      link.href = data.data.fileUrl;
      link.download = data.data.fileName || `document_${documentId}`;
      link.target = '_blank'; // Opens in a new tab if the download is blocked
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Document download failed:', error);
      throw error;
    }
  }

  async uploadDocument(file, metadata = {}) {
    const formData = new FormData();
    formData.append('clientId', this.getClient()._id);
    formData.append('file', file);
    formData.append('label', metadata.label || file.name);
    formData.append('description', metadata.description || '');
    // formData.append('is_confidential', metadata.is_confidential ? '1' : '0');

    const authHeaders = this.getAuthHeaders();
    delete authHeaders['Content-Type']; // Let browser set the correct Content-Type for FormData

    try {
      const response = await fetch(`${this.baseURL}/client-documents`, {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Document upload failed:', error);
      throw error;
    }
  }

  // Payment-related API calls
  async getWalletBalance() {
    return this.clientRequest('/client-wallets/me');
  }

  async createWalletTopUp(data) {
    return this.clientRequest('/payments/wallet/topup-intent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPaymentMethods() {
    return this.clientRequest('/payments/methods');
  }

  async getPaymentOptions(invoiceId) {
    const clientId = this.getClient()?._id;
    return this.clientRequest(`/payments/get-payment-methods/${clientId}?invoiceId=${invoiceId}`);
  }

  async processInvoicePayment(data) {
    return this.clientRequest('/payments/create-intent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Requests a GoCardless Direct Debit for the invoice's outstanding balance.
  // The server works out the amount and the mandate.
  async collectInvoiceByDirectDebit(invoiceId) {
    return this.clientRequest(`/payments/invoice/${invoiceId}/collect`, {
      method: 'POST',
    });
  }

  async payInvoice(data) {
    return this.clientRequest('/payments/create-intent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createPaymentIntent(data) {
    return this.clientRequest('/payments/create-intent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // GoCardless Mandate Management
  async getMandates() {
    return this.clientRequest('/payments/methods');
  }

  async createMandateSetup(successUrl, provider = 'gocardless') {
    try {
      // console.log('Creating mandate setup with success URL:', successUrl);
      const response = await fetch(`${this.baseURL}/payments/methods/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({ successUrl: successUrl, provider: provider }),
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create mandate setup');
      }
      return data;
    } catch (error) {
      console.error('Create mandate setup error:', error);
    }
    // return this.clientRequest(`/client-mandates.php?action=setup&success_url=${encodeURIComponent(successUrl)}`);
  }

  async completeMandateSetup(redirectFlowId, provider = 'gocardless') {
    return this.clientRequest('/payments/methods/complete-setup', {
      method: 'POST',
      body: JSON.stringify({ provider, redirectFlowId }),
    });
  }

  // Get user permissions
  async getUserPermissions() {
    return this.clientRequest('/user-permissions');
  }

  // Generic API method for new endpoints
  async api(endpoint, options = {}) {
    return this.clientRequest(endpoint, options);
  }


  // Generic client request method
  async clientRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Get auth headers
    const authHeaders = this.getAuthHeaders();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Try to parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error('Invalid server response');
      }

      // Handle authentication errors
      if (response.status === 401) {
        console.log('401 Unauthorized - clearing client auth');
        this.clearAuth();
        window.location.href = '/client/login';
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        console.error('Client API Error:', data);
        throw new Error(data.message || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('Client API Error:', error);
      throw error;
    }
  }
}

export default new ClientAuthService();